// components/common/Toast/toastBridge.ts
// Module-level bridge so non-React code (e.g. Entities.ts) can raise toasts.

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastOptions {
  message: string;
  type?: ToastType;
  /** Auto-dismiss delay in ms. Default 4500. Use 0 to keep until dismissed. */
  duration?: number;
}

type ShowFn = (opts: ToastOptions) => void;

let handler: ShowFn | null = null;

/** Registered by ToastProvider on mount. */
export const registerToastHandler = (fn: ShowFn | null): void => {
  handler = fn;
};

const show = (opts: ToastOptions): void => {
  if (handler) {
    handler(opts);
  } else {
    // Fallback before the provider mounts — never silently drop a message.
    console.warn('[toast:no-provider]', opts.message);
  }
};

/** Imperative toast API usable anywhere (components or plain modules). */
export const notify = {
  show,
  success: (message: string, duration?: number) => show({ message, type: 'success', duration }),
  error: (message: string, duration?: number) => show({ message, type: 'error', duration }),
  info: (message: string, duration?: number) => show({ message, type: 'info', duration }),
  warning: (message: string, duration?: number) => show({ message, type: 'warning', duration }),
};
